import { useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { isPast, secondsToMilliseconds } from 'date-fns';

import { selectAppState } from 'store/config/selectors.ts';
import { StreamTypes } from 'store/player/actions.types.ts';
import { selectChannels } from 'store/programs/selectors.ts';

import parseVideoItem from 'services/data-manager/parsing/parseVideoItem.ts';
import { VideoItem } from 'services/data-manager/types.ts';

import { ERROR_CODE_A201 } from 'utils/error/errorCodes.ts';
import useGetLiveProgram from 'utils/hooks/useGetLiveProgram.ts';

import { PlayerError, SwitchPlayerSource } from 'screens/PlayerModal/types.ts';

type Params = {
    playerError: PlayerError;
    videoItem: VideoItem | undefined;
    onSwitchPlayerSource: SwitchPlayerSource;
};

const UPDATE_INTERVAL = 10000;

function usePlayerBlackoutInterval({
    playerError,
    videoItem,
    onSwitchPlayerSource,
}: Params) {
    const interval = useRef<NodeJS.Timeout | undefined>(undefined);

    const channels = useSelector(selectChannels);
    const appState = useSelector(selectAppState);

    const onGetLiveProgram = useGetLiveProgram();

    useEffect(() => {
        if (
            appState === 'active' &&
            playerError.code === ERROR_CODE_A201 &&
            videoItem?.channelId &&
            videoItem.endTime
        ) {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = undefined;
            }

            interval.current = setInterval(async () => {
                if (
                    videoItem.channelId &&
                    videoItem.endTime &&
                    isPast(secondsToMilliseconds(videoItem.endTime))
                ) {
                    const channel = channels.find(
                        ({ editorial: { id } }) => id === videoItem.channelId,
                    );

                    if (channel) {
                        const liveProgram = await onGetLiveProgram(
                            channel.editorial.id,
                        );

                        onSwitchPlayerSource(
                            parseVideoItem({
                                channel,
                                program: liveProgram,
                                streamType: StreamTypes.live,
                            }),
                            StreamTypes.live,
                        );
                    }
                }
            }, UPDATE_INTERVAL);
        } else if (interval.current) {
            clearInterval(interval.current);
            interval.current = undefined;
        }
    }, [playerError, appState]);
}

export default usePlayerBlackoutInterval;
